import type { ReactNode } from 'react'
import { Icon, type IconName } from '@/components/Icon'

type Tone = 'info' | 'warning' | 'danger' | 'success'

const ICONS: Record<Tone, IconName> = {
  info: 'info',
  warning: 'alert',
  danger: 'alert',
  success: 'check',
}

const TONES: Record<Tone, string> = {
  info: 'bg-accent-soft border-accent-line [&>svg]:text-accent',
  warning: 'bg-warning-soft border-warning-line [&>svg]:text-warning',
  danger: 'bg-danger-soft border-danger-line [&>svg]:text-danger',
  success: 'bg-success-soft border-success-line [&>svg]:text-success',
}

interface Props {
  tone?: Tone
  children: ReactNode
  actions?: ReactNode
  /** Announce it when it appears without stealing focus. */
  live?: boolean
}

/** Tinted rather than filled: a solid red strip reads as an emergency even when
 *  it says "you are offline, nothing is lost". Every tone carries an icon. */
export function Banner({ tone = 'info', children, actions, live = false }: Props) {
  return (
    <div
      className={`flex items-start gap-3 rounded-md border px-4 py-3 text-sm leading-snug text-fg ${TONES[tone]}`}
      role={live ? 'status' : undefined}
      aria-live={live ? 'polite' : undefined}
    >
      <Icon name={ICONS[tone]} className="mt-[0.1em] flex-none" />
      <span className="min-w-0 flex-1">{children}</span>
      {actions && <span className="ml-auto flex flex-none gap-2">{actions}</span>}
    </div>
  )
}
